import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AppTheme } from '../../settings/appTheme';

const COOKIE_CONSENT_KEY = 'fanaticka_cookie_consent';

type CookieConsent = {
  accepted: boolean;
  timestamp: number;
};

export const CookieBanner = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    const consent: CookieConsent = {
      accepted: true,
      timestamp: Date.now(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleReject = () => {
    const consent: CookieConsent = {
      accepted: false,
      timestamp: Date.now(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <BannerContainer>
      <BannerContent>
        <BannerText>
          {t('CookieBanner.Text', {
            defaultValue:
              'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
          })}
        </BannerText>
        <BannerButtons>
          <RejectButton type="button" onClick={handleReject}>
            {t('CookieBanner.Reject', { defaultValue: 'Reject' })}
          </RejectButton>
          <AcceptButton type="button" onClick={handleAccept}>
            {t('CookieBanner.Accept', { defaultValue: 'Accept All' })}
          </AcceptButton>
        </BannerButtons>
      </BannerContent>
    </BannerContainer>
  );
};

const BannerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: ${AppTheme.maxWidth};
  z-index: 999;
  padding: 16px;
  pointer-events: none;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const BannerContent = styled.div`
  background: ${AppTheme.colors.bg};
  border: 2px solid ${AppTheme.colors.accent};
  border-radius: 16px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  pointer-events: auto;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  @media (max-width: 768px) {
    padding: 16px 20px;
    gap: 12px;
  }
`;

const BannerText = styled.p`
  font-family: ${AppTheme.fonts.regularFont};
  font-size: 14px;
  line-height: 1.5;
  color: ${AppTheme.colors.text};
  margin: 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const BannerButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const AcceptButton = styled.button`
  padding: 10px 24px;
  border-radius: 999px;
  background: ${AppTheme.colors.accent};
  border: none;
  color: #0b0b0b;
  font-family: ${AppTheme.fonts.regularFont};
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0.04em;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${AppTheme.colors.accentDimmed};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 24px;
  }
`;

const RejectButton = styled.button`
  padding: 10px 24px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid ${AppTheme.colors.text};
  color: ${AppTheme.colors.text};
  font-family: ${AppTheme.fonts.regularFont};
  font-size: 14px;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: ${AppTheme.colors.accent};
    color: ${AppTheme.colors.accent};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 24px;
  }
`;
